import React, { useEffect, useRef, useState } from 'react';
import PubSub from 'pubsub-js';
import PlunkChat from '../plunkchat/PlunkChat';
import { ensureFreshToken } from '../endpoints/market-intelligence';
import account from '../data/account';

// Ask Plunk — the App host for <PlunkChat> (AI_CHAT_UX §5; the
// assistant is APP-ONLY per the 2026-08-17 decision). Right slide-in
// panel over the current view (the MI ai-drawer pattern users know):
// ~520px, one-click widen to ~880px, full-screen sheet under 960px,
// Esc/backdrop close, body scroll lock. Open state persists within the
// session; the last conversation resumes across sessions.
//
// Launcher: the toolbar sparkle icon publishes 'plunk-assistant-toggle';
// this component (mounted once at the app root) subscribes.
const CHAT_API = (() => {
  try {
    return localStorage.getItem('plunk-app-chat-api') || 'https://chat.plunk.io';
  } catch (e) {
    return 'https://chat.plunk.io';
  }
})();
const CONVO_KEY = 'plunk-app-chat-convo';
const OPEN_KEY = 'plunk-app-assistant-open';
async function chatToken() {
  const token = await ensureFreshToken();
  return token || '';
}
export default function AssistantPanel() {
  const [open, setOpen] = useState(() => {
    try {
      return sessionStorage.getItem(OPEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const [wide, setWide] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  // §284 — a toggle event may carry a starter prompt (onboarding "ask
  // Plunk to do it" affordances): open the panel and prime the composer;
  // the user still presses send (suggestion, never action).
  const [prime, setPrime] = useState(null);
  const fetchedRef = useRef(false);
  const [conversationId, setConversationId] = useState(() => {
    try {
      return localStorage.getItem(CONVO_KEY) || null;
    } catch (e) {
      return null;
    }
  });
  useEffect(() => {
    const sub = PubSub.subscribe('plunk-assistant-toggle', (msg, data) => {
      if (data && data.starter) {
        setPrime({
          text: String(data.starter),
          ts: Date.now()
        });
        setOpen(true);
        try {
          sessionStorage.setItem(OPEN_KEY, '1');
        } catch (e) {/* private mode */}
        return;
      }
      setOpen(prev => {
        const v = !prev;
        try {
          sessionStorage.setItem(OPEN_KEY, v ? '1' : '0');
        } catch (e) {/* private mode */}
        return v;
      });
    });
    return () => PubSub.unsubscribe(sub);
  }, []);
  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(OPEN_KEY, '0');
    } catch (e) {/* private mode */}
  };

  // Capability-aware starters — fetched once per mount from the service.
  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const token = await chatToken();
        const res = await fetch(`${CHAT_API}/v1/chat/suggestions`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (res.ok) setSuggestions((await res.json()).suggestions || []);
      } catch (e) {/* empty state still works without starters */}
    })();
  }, [open]);

  // Esc close + body scroll lock while open (the ai-drawer pattern).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);
  if (!open) return null;
  const company = account.get().company;
  const onConvoChange = id => {
    setConversationId(id);
    try {
      if (id) localStorage.setItem(CONVO_KEY, id);else localStorage.removeItem(CONVO_KEY);
    } catch (e) {/* private mode */}
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "pchat-host",
    role: "dialog",
    "aria-label": "Ask Plunk"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pchat-host-backdrop",
    onClick: close,
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("aside", {
    className: `pchat-host-panel${wide ? ' pchat-host-panel--wide' : ''}`
  }, /*#__PURE__*/React.createElement("header", {
    className: "pchat-host-header"
  }, /*#__PURE__*/React.createElement("span", {
    className: "pchat-host-dot"
  }), /*#__PURE__*/React.createElement("h2", null, "Ask Plunk"), /*#__PURE__*/React.createElement("div", {
    className: "pchat-host-actions"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pchat-host-btn pchat-host-widen",
    onClick: () => setWide(w => !w),
    title: wide ? 'Narrow panel' : 'Widen panel'
  }, /*#__PURE__*/React.createElement("i", {
    className: "material-icons"
  }, wide ? 'close_fullscreen' : 'open_in_full')), /*#__PURE__*/React.createElement("button", {
    className: "pchat-host-btn",
    onClick: close,
    title: "Close (Esc)"
  }, /*#__PURE__*/React.createElement("i", {
    className: "material-icons"
  }, "close")))), /*#__PURE__*/React.createElement("div", {
    className: "pchat-host-body"
  }, /*#__PURE__*/React.createElement(PlunkChat, {
    apiBase: CHAT_API,
    auth: chatToken,
    extraHeaders: {
      'x-plunk-app': 'analytics'
    },
    conversationId: conversationId,
    onConversationChange: onConvoChange,
    placeholder: "Ask about your data\u2026",
    suggestions: suggestions,
    welcomeTitle: company ? `Ask me anything about ${company}'s data.` : 'Ask me anything about your data.',
    welcomeSub: "I answer with live numbers and always name the range I checked.",
    theme: "app",
    primeText: prime
  }))));
}